import React, { useState, useRef, useEffect } from "react";
import treeObjects from "../../../Assets/Tree-OBJECTS.png";
import chatbotImage from "../../../Assets/ChatBot-Group 7.png";
import ImageComponent from "../../Image/ImageComponent";
import sendIcon from "../../../Assets/Send Icon.png";
import fileclip from "../../../Assets/fileclip.png";
import { sendChatMessage, analysePlant } from "../../../API/services/ChatApi";
import TypingEffect from "../../Typing/TypingEffect";

const allowedFileTypes = ["image/png", "image/jpg", "image/jpeg", "image/gif"];

const VirtualArborist = () => {
  const [messages, setMessages] = useState([
    { sender: "bot", text: "Hi! I am Your Tree Guy!" },
    { sender: "bot", text: "I'm here to assist you." },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const chatContainerRef = useRef(null);
  const [completedMessages, setCompletedMessages] = useState([]);

  const exampleQuestions = [
    "How much should I water my trees?",
    "What trees are native to the Sierras?",
    "How can I tell if my tree is dying?",
  ];

  const [isUserScrolling, setIsUserScrolling] = useState(false);

  // Function to check if the user is near the bottom
  const isNearBottom = () => {
    if (!chatContainerRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    return scrollHeight - scrollTop <= clientHeight + 5; // Buffer of 50px
  };

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    // Handle manual scrolling
    const handleScroll = () => {
      setIsUserScrolling(!isNearBottom());
    };

    chatContainer.addEventListener("scroll", handleScroll);

    return () => {
      chatContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isUserScrolling) {
      chatContainerRef.current?.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    // Observe changes in chat content
    const observer = new MutationObserver(() => {
      if (!isUserScrolling) {
        chatContainerRef.current?.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    });

    if (chatContainerRef.current) {
      observer.observe(chatContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [messages, isUserScrolling]);

  const handleSendMessage = async () => {
    if (!input.trim() || isUploading) return;

    const userMessage = { sender: "user", text: input };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput("");

    setIsTyping(true);

    try {
      const botResponse = await sendChatMessage({ query: input });
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: botResponse.output || "I'm sorry, I couldn't process that.",
        },
        ...(botResponse?.follow_up_message &&
        botResponse.follow_up_message !== ""
          ? [
              {
                sender: "bot",
                text: botResponse.follow_up_message,
              },
            ]
          : []),
      ]);
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "Something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleExampleClick = (example) => {
    setInput(example);
  };

  const handleFileUpload = async (event) => {
    setIsTyping(true);
    const fileInput = event.target;
    const file = fileInput.files[0];
    if (!file) return;

    // Validate file type
    if (!allowedFileTypes.includes(file.type)) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "Invalid file type! Please upload a .png, .jpg, .jpeg, or .gif file.",
        },
      ]);
      fileInput.value = ""; // Reset file input
      return;
    }

    setIsUploading(true);
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "user",
        text: (
          <img src={URL.createObjectURL(file)} className="w-[50%]" alt="img" />
        ),
        type: "html",
      },
    ]);

    try {
      const response = await analysePlant(file);
      if (response) {
        const { disease, is_plant, is_healthy, species_name, common_names } =
          response;
        let responseText = `<div class="bg-gray-100 p-2 md:p-6 shadow-lg">`;
        if (is_plant.binary === true) {
          responseText += `
          <div class="bg-gray-100 rounded-lg sm:max-w-3xl mx-auto">
            <h2 class="text-2xl font-bold text-center text-green-700 mb-4">🌿 Plant Health Analysis Report</h2>
        `;

          responseText += `
<div class="flex flex-row items-center space-x-2 text-lg text-gray-600 mb-4">
  <p class="text-nowrap text-xl font-bold">Plant Species:</p>
  <p class="text-green-500 font-semibold text-lg text-center">
    ${species_name}
  </p>
</div>
      `;
          // common names
          responseText += `
        ${
          common_names
            ? `<div class="flex flex-row items-center space-x-2 text-lg text-gray-600 mb-4">
      <p class="text-nowrap text-xl font-bold">Common Names:</p>
      <p class="text-green-500 font-semibold text-lg text-center">
      ${common_names.join(", ")}
      </p>
      </div>`
            : ""
        }
      `;
        }

        if (is_plant.binary === "False") {
          responseText += `
            <p class="text-red-500 bg-gray-100 p-2 font-semibold text-lg text-center">
              ❌ The uploaded image does not appear to be a plant.
            </p>
          `;
        } else if (is_healthy.binary) {
          responseText += `
            <p class="text-green-600 font-semibold text-lg text-center">
              ✅ Your plant looks healthy! (Probability: ${(
                is_healthy.probability * 100
              ).toFixed(2)}%)
            </p>
          `;
        } else if (disease.length > 0) {
          responseText += `
            <p class="text-red-600 font-semibold text-lg text-center">
              ❌ The plant appears unhealthy. Below are the identified conditions:
            </p>
            <div class="mt-4 space-y-6">
          `;

          disease.forEach((d, index) => {
            responseText += `
              <div class="md:p-4 bg-white rounded-lg shadow">
                <h3 class="text-lg font-bold text-gray-800">🔹 Disease ${
                  index + 1
                }: ${d.name}</h3>
                <p class="text-gray-700"><strong>📊 Probability:</strong> ${(
                  d.probability * 100
                ).toFixed(2)}%</p>
                <p class="text-gray-700"><strong>📖 Description:</strong> ${
                  d.details.description
                }</p>
                
                <div class="mt-4">
                  <h4 class="font-semibold text-gray-800">🛠️ Treatment Recommendations:</h4>
            `;

            if (d.details.treatment.chemical?.length > 0) {
              responseText += `<p class="text-gray-700"><strong>🧪 Chemical Treatment:</strong></p><ul class="list-disc pl-6">`;
              d.details.treatment.chemical.forEach((treatment) => {
                responseText += `<li class="text-gray-700">${treatment}</li>`;
              });
              responseText += `</ul>`;
            } else {
              responseText += `<p class="text-gray-700"><strong>🧪 Chemical Treatment:</strong> Not available</p>`;
            }

            if (d.details.treatment.biological.length > 0) {
              responseText += `<p class="text-gray-700"><strong>🦠 Biological Treatment:</strong></p><ul class="list-disc pl-6">`;
              d.details.treatment.biological.forEach((treatment) => {
                responseText += `<li class="text-gray-700">${treatment}</li>`;
              });
              responseText += `</ul>`;
            } else {
              responseText += `<p class="text-gray-700"><strong>🦠 Biological Treatment:</strong> Not available</p>`;
            }

            if (d.details.treatment.prevention.length > 0) {
              responseText += `<p class="text-gray-700"><strong>🛡️ Preventive Measures:</strong></p><ul class="list-disc pl-6">`;
              d.details.treatment.prevention.forEach((measure) => {
                responseText += `<li class="text-gray-700">${measure}</li>`;
              });
              responseText += `</ul>`;
            } else {
              responseText += `<p class="text-gray-700"><strong>🛡️ Preventive Measures:</strong> Not available</p>`;
            }

            responseText += `
                </div>
              </div>
            `;
          });

          responseText += `
            </div>
            <div class="mt-6 md:p-4 bg-green-100 rounded-lg shadow">
              <h4 class="text-lg font-bold text-green-700">🔔 Final Recommendation:</h4>
              <ul class="list-disc pl-6 text-green-700">
                <li>✔️ Immediate action is advised to control pests and prevent further damage.</li>
                <li>✔️ Consider <strong>biological treatments first</strong> before using chemical solutions.</li>
                <li>✔️ Regular inspection and preventive care can improve plant health.</li>
              </ul>
            </div>
          `;
        } else {
          responseText += `
            <p class="text-yellow-600 font-semibold text-lg text-center">
              ⚠️ No specific disease detected, but keep an eye on your plant.
            </p>
          `;
        }

        responseText += `</div>`;

        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: "bot", text: responseText, type: "html" },
        ]);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            text: '<div class="text-center text-red-500 font-semibold">Error analyzing the image. Please try again.</div>',
          },
        ]);
      }
    } catch (error) {
      console.log(error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: "Something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setIsUploading(false);
      fileInput.value = ""; // Reset file input
      setIsTyping(false);
    }
  };

  const isImageTag = (message) => {
    return message.type === "img";
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
      style={{
        backgroundImage: `url('${treeObjects}')`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right bottom",
        backgroundSize: "contain",
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault(); // Prevent default behavior (e.g., form submission)
          handleSendMessage();
        }
      }}
    >
      <h1 className="heading text-[#0b3d2c] my-10">YOUR VIRTUAL ARBORIST</h1>

      {/* Chat Messages */}
      <div
        className="w-full md:w-[50%] p-4 rounded-lg mb-4 max-h-[400px] overflow-y-auto"
        ref={chatContainerRef}
      >
        {messages.map((message, index) => {
          const shouldRender =
            index === 0 || completedMessages.includes(index - 1);

          return (
            <div
              key={index}
              className={`flex ${
                message.type === "html" ? "flex-col md:flex-row" : "flex-row"
              }
         w-full ${
           message.sender === "bot" ? "justify-start" : "justify-end"
         } mb-4`}
            >
              {message.sender === "bot" && (
                <>
                  {(index === 0 ||
                    messages[index - 1].sender !== message.sender) && (
                    <div className="w-12 h-12 aspect-square bg-green-600 rounded-full flex items-center justify-center mr-4">
                      <ImageComponent
                        src={chatbotImage}
                        alt="chatbot"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </>
              )}

              {shouldRender && (
                <div
                  className={`${
                    message?.type ? (message.type === "html" ? "mt-2" : "") : ""
                  }
            ${
              index !== 0 &&
              messages[index - 1].sender === messages[index].sender
                ? message.sender === "bot"
                  ? "ml-[64px]"
                  : ""
                : ""
            }
            ${
              message.sender === "bot"
                ? ` ${message.type === "html" ? "" : "bg-[#ededed]"}`
                : `${!isImageTag(message.text) && "bg-green-600"} text-white`
            }
            rounded-lg ${message.type === "html" ? "" : "shadow p-3"} text-sm
            ${
              !isImageTag(message.text)
                ? "max-w-full sm:max-w-[70%]"
                : "sm:max-w-[50%] w-[80%] flex justify-center"
            }`}
                >
                  {shouldRender && (
                    <TypingEffect
                      text={message.text}
                      type={message?.type || "text"}
                      speed={5}
                      isNotEffect={message.sender === "bot" ? false : true}
                      className={`text-sm flex items-center justify-center ${
                        message.type === "html" ? "w-full" : ""
                      }`}
                      onComplete={() =>
                        setCompletedMessages((prev) => [...prev, index])
                      }
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
              <ImageComponent src={chatbotImage} alt={"chatbotImage"} />
            </div>
            <div className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 p-4 rounded-xl shadow-lg text-sm max-w-[70%] relative flex items-center">
              <div className=" bottom-[-8px] left-3 flex space-x-1">
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-200"></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input & Upload */}
      <div className="w-full md:w-[50%] relative flex items-center">
        <input
          type="file"
          accept=".png, .jpg, .jpeg, .gif"
          onChange={handleFileUpload}
          className="absolute left-1 md:left-4 top-1/2 transform -translate-y-1/2 w-10 md:w-8 h-5 opacity-0 cursor-pointer z-10 "
        />
        <ImageComponent
          src={fileclip}
          alt="fileclip"
          className="absolute left-4 w-5 h-5 cursor-pointer"
        />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask me anything about your trees.."
          disabled={isUploading}
          className="flex-1 px-10 w-full md:w-[50%] md:px-12 py-4 border border-green-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-[#f8f5f0]"
        />

        <ImageComponent
          src={sendIcon}
          alt="sendIcon"
          className="absolute right-4 w-5 h-5 cursor-pointer"
          onClick={handleSendMessage}
        />
      </div>

      {/* Example Questions */}
      <div className="flex flex-wrap justify-center gap-2 my-4">
        {exampleQuestions.map((question, index) => (
          <button
            key={index}
            onClick={() => handleExampleClick(question)}
            className="text-gray-700 bg-white border-2 px-4 py-2 rounded-full text-sm hover:bg-gray-200"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VirtualArborist;
